import { Group, Loader, Stack, Title } from "@mantine/core";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import useCourseCategories from "../../../../api/useCourseCategories";
import { useCourses } from "../../../../api/useCourses";
import QueryError from "../../../../components/QueryError/QueryError";
import UncategorizedCoursesInformation from "../../../../components/UncategorizedCoursesInformation/UncategorizedCoursesInformation";
import AddCategory from "../../../../features/AddCategory/AddCategory";
import CourseCategories from "../../../../features/Courses/CourseCategories/CourseCategories";
import CoursesGrid from "../../../../features/Courses/CoursesGrid/CoursesGrid";
import CustomCoursesEmptyState from "../../../../features/Courses/CustomCoursesEmptyState/CustomCoursesEmptyState";
import YKBTeacherInstruction from "../../../../features/Courses/YKBTeacherInstruction/YKBTeacherInstruction";
import AdminOnly from "../../../../features/Layout/AdminOnly/AdminOnly";
import SearchingFor from "../../../../features/Layout/SearchingFor/SearchingFor";
import { categorySearchSchema } from "../../../../helpers/search.helpers";
import useCourseCategorySearch from "../../../../hooks/useCourseCategorySearch";
import useIsYKBCategory from "../../../../hooks/useIsYKBCategory";
import useSkillsterStore from "../../../../store/app";

const coursesSearchSchema = categorySearchSchema.extend({
  // Set when leaving the course editor, see useExitAndOverrideBlocker.
  overrideBlocker: fallback(z.boolean().optional(), undefined),
});

export const Route = createFileRoute("/(app)/courses/$type/")({
  component: RouteComponent,
  validateSearch: zodValidator(coursesSearchSchema),
});

function RouteComponent() {
  const { type } = useParams({ strict: false });
  return type === "native" ? <NativeCourses /> : <CustomCourses />;
}

function NativeCourses() {
  const { t } = useTranslation();
  const filter = useSkillsterStore((state) => state.filters).get("courses");
  const showCategories = filter?.length === 0 || !filter;
  const { categoryId, setCategoryId } = useCourseCategorySearch();
  const isYKBCategory = useIsYKBCategory(categoryId);
  // The YKB modules get one shared heading instead of the linear/open ones.
  const showYKB = showCategories && isYKBCategory;
  const coursesQuery = useCourses();
  const categoriesQuery = useCourseCategories();

  // The category list and both grids share these queries, so show one error
  // for the page instead of one per section.
  if (coursesQuery.isError || categoriesQuery.isError)
    return (
      <QueryError
        error={coursesQuery.error ?? categoriesQuery.error}
        onRetry={() => {
          if (coursesQuery.isError) coursesQuery.refetch();
          if (categoriesQuery.isError) categoriesQuery.refetch();
        }}
        isRetrying={coursesQuery.isRefetching || categoriesQuery.isRefetching}
      />
    );

  return (
    <Stack>
      {showCategories && (
        <Group justify="space-between">
          <CourseCategories
            editable
            selectedCategoryId={categoryId}
            onSelect={setCategoryId}
          />
          <AdminOnly restrictToSkillsterOrg>
            <AddCategory />
          </AdminOnly>
        </Group>
      )}
      <UncategorizedCoursesInformation categoryId={categoryId} />
      {showYKB && <YKBTeacherInstruction />}
      <SearchingFor filter={filter} />
      {showYKB && (
        <Title order={4} mb="-lg">
          {t("ykb.modulesTitle")}
        </Title>
      )}
      <CoursesGrid
        variant="native"
        filter={filter}
        courseType="linear"
        categoryId={categoryId}
        hideHeader={showYKB}
      />
      <CoursesGrid
        variant="native"
        filter={filter}
        courseType="open"
        categoryId={categoryId}
        hideHeader={showYKB}
      />
    </Stack>
  );
}

function CustomCourses() {
  const { data, isPending, isError, error, refetch, isRefetching } =
    useCourses();
  const filter = useSkillsterStore((state) => state.filters).get("courses");
  const hasCustomCourses = !!data?.list.filter((course) => !course.Native)
    ?.length;

  // Without this an error falls through to the "no custom courses" empty state.
  if (isError)
    return (
      <QueryError
        error={error}
        onRetry={() => refetch()}
        isRetrying={isRefetching}
      />
    );

  return (
    <>
      {isPending ? (
        <Loader />
      ) : !hasCustomCourses ? (
        <CustomCoursesEmptyState />
      ) : (
        <Stack>
          <SearchingFor filter={filter} />
          <CoursesGrid
            variant={"custom"}
            filter={filter}
            courseType="linear"
            categoryId={null}
          />
          <CoursesGrid
            variant={"custom"}
            filter={filter}
            courseType="open"
            categoryId={null}
          />
        </Stack>
      )}
    </>
  );
}
